import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  TextField,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import React, { FC, useEffect } from 'react';
import { grey } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useUnit } from 'effector-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  $openWorkspaceCreate,
  onSetOpenWorkspaceCreate,
  workspaceCreateFX,
  $enterprises,
  enterprisesGetFX,
} from '../../../../../model/workspace';

export const WorkspaceCreate: FC = () => {
  const openWorkspaceCreate = useUnit($openWorkspaceCreate);
  const pending = useUnit(workspaceCreateFX.pending);
  const enterprises = useUnit($enterprises);
  const loadingEnterprises = useUnit(enterprisesGetFX.pending);
  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
      enterpriseId: '' as unknown as number | '',
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .required('Обязательное поле')
        .trim()
        .min(3, 'Описание слишком короткое'),
      enterpriseId: Yup.number()
        .typeError('Выберите предприятие')
        .required('Выберите предприятие'),
    }),
    validateOnBlur: false,
    validateOnChange: true,
    validateOnMount: false,
    onSubmit: (values, f) => {
      const { title, description, enterpriseId } = values;
      workspaceCreateFX({
        fields: { title, description, enterpriseId: enterpriseId as number, },
      }).then(() => {
        onSetOpenWorkspaceCreate(false);
        f.resetForm();
      });
    },
  });
  useEffect(() => {
    if (!openWorkspaceCreate) return;
    enterprisesGetFX(undefined);
  }, [openWorkspaceCreate]);

  useEffect(() => {
    if (!openWorkspaceCreate) return;
    if (enterprises.length === 1 && !formik.values.enterpriseId) {
      formik.setFieldValue('enterpriseId', enterprises[0].id, true);
    }
  }, [openWorkspaceCreate, enterprises]);
  
  const handleClose = () => {
    onSetOpenWorkspaceCreate(false);
    formik.resetForm();
  };

  const disabled =
    Boolean(formik.errors.title) ||
    !formik.values.title ||
    Boolean(formik.errors.enterpriseId) ||
    !formik.values.enterpriseId ||
    pending;
  return (
    <Dialog
      open={openWorkspaceCreate}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
    >
      <DialogTitle>
        Создайте рабочее пространство
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Повысьте производительность: участники команды смогут получать удобный
          доступ ко всем доскам.
        </DialogContentText>
        <FormControl variant="standard" fullWidth margin="dense">
          <InputLabel id="enterprise-label">Предприятие</InputLabel>
          <Select
            labelId="enterprise-label"
            id="enterpriseId"
            name="enterpriseId"
            label="Предприятие"
            value={formik.values.enterpriseId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.errors.enterpriseId)}
            disabled={loadingEnterprises}
          >
            <MenuItem value="" disabled>
              {loadingEnterprises ? 'Загрузка…' : 'Выберите предприятие'}
            </MenuItem>
            {enterprises.map((e) => (
              <MenuItem key={e.id} value={e.id}>
                {e.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl variant="standard" fullWidth margin="dense">
          <TextField
            autoFocus
            inputProps={{ maxLength: 255 }}
            margin="dense"
            id="title"
            label="Название рабочего пространства"
            name="title"
            onChange={formik.handleChange}
            value={formik.values.title}
            onBlur={formik.handleBlur}
            error={Boolean(formik.errors.title)}
            helperText={
              formik.errors.title ||
              'Укажите название вашей команды, компании или организации.'
            }
          />
        </FormControl>

        <FormControl variant="standard" fullWidth margin="dense">
          <TextField
            inputProps={{ maxLength: 4096 }}
            margin="dense"
            fullWidth
            label="Описание рабочего пространства (необязательно)"
            multiline
            minRows={5}
            sx={{
              '& textarea': {
                resize: 'none',
              },
            }}
            id="description"
            name="description"
            onChange={formik.handleChange}
            value={formik.values.description}
            onBlur={formik.handleBlur}
            helperText="Расскажите участникам немного о рабочем пространстве."
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button type="reset" onClick={handleClose}>
          Отменить
        </Button>
        <Button
          type="submit"
          variant="contained"
          onClick={formik.submitForm}
          disabled={disabled}
        >
          Создать
        </Button>
      </DialogActions>
    </Dialog>
  );
};
